import React from 'react';
import isNil from 'lodash/isNil';
import { Loader } from 'semantic-ui-react';
import { useQuery } from '@apollo/client';
import { populateBuilderInfoAction } from '../../../../redux/slices/analyses';
import {
  IGetAnalysisInfoQuery,
  IGetAnalysisInfoQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import BuilderInfo from '../../../BuilderInfo';
import { VersionedEntity } from '../../../EntityLabels';
import { useAppDispatch } from '../../../../redux/hooks';
import { GET_ANALYSIS_INFO } from '../../../../utils/apollo/analysis';
import { assertNotNil } from '../../../../utils/types';
import useAnalysisBuilderInfo from '../../../../redux/selectorHooks/useAnalysisBuilderInfo';
import { theme } from '../../../../styles/theme';

/**
 * This component handles analysis information for Expression, Funnel,
 * and Retention analysis in cohort row
 *
 * By default, it will try to get information from redux.
 * If nothing found - GET_ANALYSIS query would be performed and result of it
 * would be added to redux and used as data for Info Popover.
 */

interface IProps {
  analysisId: string;
  onClear?: () => void;
}

export default ({ analysisId, onClear }: IProps) => {
  const dispatch = useAppDispatch();

  const analysisBuilderInfo = useAnalysisBuilderInfo(analysisId);

  const handleLoadAnalysisInfo = ({ analysis }: IGetAnalysisInfoQuery) => {
    assertNotNil(analysis);
    dispatch(populateBuilderInfoAction({ analysisInfo: analysis }));
  };

  const { loading } = useQuery<
    IGetAnalysisInfoQuery,
    IGetAnalysisInfoQueryVariables
  >(GET_ANALYSIS_INFO, {
    skip: !isNil(analysisBuilderInfo),
    variables: {
      analysisId,
    },
    onCompleted: handleLoadAnalysisInfo,
  });

  if (loading || !analysisBuilderInfo) {
    return <Loader active inline size="tiny" />;
  }

  const { id, queryType, display } = analysisBuilderInfo;

  return (
    <VersionedEntity
      onClear={onClear}
      label={display}
      labelColor={theme.colors.secondary900}
      isLatestVersion={true}
    >
      <BuilderInfo kind="report" analysisId={id} queryType={queryType} />
    </VersionedEntity>
  );
};
